/* Mock Sub-Supplier Webshop · used by the UniBuy™ demo to simulate
   the second jump in the double-jump PunchOut flow.

   The buyer arrives here after clicking a sub-supplier tile inside
   UniBuy™. They pick items from a curated catalog, then hit "Send
   Cart to UniBuy™" — which calls the mock-cart endpoint. That
   endpoint synthesises a real PunchOutOrderMessage cXML and replays
   it through the same inbound parser that real partners would hit, so
   the entire roundtrip exercises production code paths.

   Route: /infoshop/punchout/mock-supplier?session_id=…&supplier_id=…
*/
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Plus, Minus, Trash2, Send, Loader2, Search, Store,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Curated mock catalog keyed by supplier code. Real life: would be the
// sub-supplier's own webshop UI. For the demo we ship 8-10 items per
// supplier so the buyer has something realistic to add to their cart.
const MOCK_CATALOGS = {
  FAST: [
    { sku: "FAST-HEX-M10-50", mfg: "WURTH-HEX-M10x50", desc: "Hex bolt M10×50 zinc plated", price: 1.85, uom: "EA", category: "Fasteners", unspsc: "31161501", lead: 3 },
    { sku: "FAST-HEX-M12-60", mfg: "WURTH-HEX-M12x60", desc: "Hex bolt M12×60 zinc plated", price: 2.40, uom: "EA", category: "Fasteners", unspsc: "31161501", lead: 3 },
    { sku: "FAST-NUT-M10",    mfg: "WURTH-NUT-M10",    desc: "Hex nut M10 zinc plated · DIN 934", price: 0.35, uom: "EA", category: "Fasteners", unspsc: "31161604", lead: 3 },
    { sku: "FAST-WASH-M10",   mfg: "WURTH-WSH-M10",    desc: "Flat washer M10 · DIN 125", price: 0.18, uom: "EA", category: "Fasteners", unspsc: "31161706", lead: 3 },
    { sku: "FAST-SHCS-M8-25", mfg: "BOSSARD-SHCS-M8x25", desc: "Socket head cap screw M8×25 A2", price: 0.92, uom: "EA", category: "Fasteners", unspsc: "31161520", lead: 4 },
    { sku: "FAST-ANCHOR-M10", mfg: "HILTI-HKD-M10",    desc: "Drop-in anchor M10 zinc", price: 4.10, uom: "EA", category: "Anchors", unspsc: "31162002", lead: 5 },
    { sku: "FAST-CLAMP-25",   mfg: "OETIKER-25",       desc: "Hose clamp 25mm stainless", price: 2.20, uom: "EA", category: "Plumbing", unspsc: "40142339", lead: 4 },
    { sku: "FAST-CABLE-TIE",  mfg: "PANDUIT-PLT2S",    desc: "Cable tie 200mm black UV", price: 0.08, uom: "EA", category: "Electrical", unspsc: "39121701", lead: 2 },
  ],
  GRAINGER: [
    { sku: "GRN-N95-50", mfg: "3M-8210", desc: "3M N95 Particulate Respirator Pack-20", price: 28.40, uom: "PK", category: "Safety (PPE)", unspsc: "46181701", lead: 4 },
    { sku: "GRN-GLOVE-CR", mfg: "ANSELL-11-845", desc: "HyFlex Cut Resistant Glove Sz-9", price: 8.20, uom: "PR", category: "Safety (PPE)", unspsc: "46181504", lead: 4 },
    { sku: "GRN-HARD-HAT", mfg: "MSA-V-GARD-WHT", desc: "MSA V-Gard Hard Hat White Class-E", price: 24.50, uom: "EA", category: "Safety (PPE)", unspsc: "46181604", lead: 5 },
    { sku: "GRN-EYE-PRO", mfg: "UVEX-S3200X", desc: "Genesis Safety Glasses Clear Lens", price: 9.80, uom: "EA", category: "Safety (PPE)", unspsc: "46181801", lead: 4 },
    { sku: "GRN-EARMUFF", mfg: "3M-1426", desc: "3M PELTOR Optime Earmuff 24dB", price: 22.10, uom: "EA", category: "Safety (PPE)", unspsc: "46181906", lead: 5 },
    { sku: "GRN-LOTO-PAD", mfg: "BRADY-99560", desc: "Safety lockout padlock keyed different", price: 18.50, uom: "EA", category: "Safety", unspsc: "46171501", lead: 6 },
  ],
  ZKH: [
    { sku: "ZKH-BEARING-6204", mfg: "SKF-6204-2RS", desc: "Deep groove ball bearing 20×47×14 sealed", price: 95.0, uom: "EA", category: "Power Transmission", unspsc: "23153105", lead: 7 },
    { sku: "ZKH-FILTER-1234",  mfg: "DONALDSON-P181134", desc: "Air filter primary radial seal", price: 220.0, uom: "EA", category: "Filtration", unspsc: "40161501", lead: 6 },
    { sku: "ZKH-OIL-DTE-25",   mfg: "MOBIL-DTE-25-208L", desc: "Hydraulic oil DTE 25 ISO VG46 208L drum", price: 5180.0, uom: "EA", category: "Lubrication", unspsc: "15121502", lead: 10 },
    { sku: "ZKH-LOCT-243-50",  mfg: "HENKEL-LOCT-243-50ml", desc: "Loctite 243 medium strength threadlocker 50ml", price: 280.0, uom: "EA", category: "Chemicals", unspsc: "31201524", lead: 5 },
  ],
};

function CatalogItem({ item, qty, onChangeQty, supplier }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`mock-item-${item.sku}`}>
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
          {supplier?.logo_url ? (
            <img src={supplier.logo_url} alt="" className="h-7 w-7 object-contain" />
          ) : (
            <Store size={20} className="text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{item.category}</div>
          <div className="font-display font-semibold text-slate-900 text-sm leading-snug mt-0.5">{item.desc}</div>
          <div className="text-[11px] font-mono text-slate-500 mt-0.5">{item.sku} · MFG {item.mfg}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-slate-900">{supplier?.currency} {item.price.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500">{item.uom} · Lead {item.lead} days</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="inline-flex items-center border border-slate-300 rounded-md overflow-hidden">
          <button
            onClick={() => onChangeQty(Math.max(0, qty - 1))}
            data-testid={`mock-qty-dec-${item.sku}`}
            className="px-2.5 py-1 text-slate-600 hover:bg-slate-50"
          >
            <Minus size={12} />
          </button>
          <input
            type="number"
            value={qty}
            onChange={(e) => onChangeQty(Math.max(0, parseInt(e.target.value || "0", 10)))}
            min={0}
            data-testid={`mock-qty-input-${item.sku}`}
            className="w-14 text-center text-sm font-medium border-0 focus:outline-none"
          />
          <button
            onClick={() => onChangeQty(qty + 1)}
            data-testid={`mock-qty-inc-${item.sku}`}
            className="px-2.5 py-1 text-slate-600 hover:bg-slate-50"
          >
            <Plus size={12} />
          </button>
        </div>
        <Button
          onClick={() => onChangeQty(qty > 0 ? qty + 1 : 1)}
          data-testid={`mock-add-${item.sku}`}
          className="h-8 bg-slate-900 hover:bg-slate-800 text-white text-xs"
        >
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

export default function MockSubSupplierPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const supplierId = params.get("supplier_id");
  const [supplier, setSupplier] = useState(null);
  const [qtys, setQtys] = useState({});
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [returned, setReturned] = useState(null);
  // Demo tax mode — lets the operator toggle between "supplier sends no tax"
  // (default, matches Grainger Advantage / VWR Contract behaviour) and
  // "supplier sends inclusive tax". Drives the <Tax> element in the
  // synthesised PunchOutOrderMessage so the UniBuy™ cart can show the
  // matching badge (v3.8.6).
  const [taxMode, setTaxMode] = useState("none"); // "none" | "incl"
  const [taxPct, setTaxPct] = useState(21);

  useEffect(() => {
    (async () => {
      if (!supplierId) return;
      try {
        const { data } = await axios.get(`${API}/infoshop/sub-suppliers`);
        const found = (data.sub_suppliers || []).find((s) => s.id === supplierId);
        setSupplier(found || null);
      } catch {
        setSupplier(null);
      }
    })();
  }, [supplierId]);

  const catalog = useMemo(() => {
    const code = supplier?.code;
    const items = (code && MOCK_CATALOGS[code]) || [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(
      (it) =>
        it.sku.toLowerCase().includes(q) ||
        it.mfg.toLowerCase().includes(q) ||
        it.desc.toLowerCase().includes(q) ||
        it.category.toLowerCase().includes(q)
    );
  }, [supplier, search]);

  const cartItems = useMemo(() => {
    const code = supplier?.code;
    const items = (code && MOCK_CATALOGS[code]) || [];
    return items
      .filter((it) => (qtys[it.sku] || 0) > 0)
      .map((it) => ({ ...it, qty: qtys[it.sku] }));
  }, [qtys, supplier]);

  const cartTotal = cartItems.reduce((s, it) => s + it.price * it.qty, 0);

  const sendCartBack = async () => {
    if (!sessionId || !supplierId || cartItems.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        items: cartItems.map((it) => {
          const lineNet = it.price * it.qty;
          const taxAmt = taxMode === "incl" ? Number(((lineNet * taxPct) / 100).toFixed(2)) : 0;
          return {
            external_sku: it.sku,
            mfg_pn: it.mfg,
            description: it.desc,
            brand: it.mfg.split("-")[0],
            unit_price: it.price,
            currency: supplier?.currency || "USD",
            quantity: it.qty,
            uom: it.uom,
            category: it.category,
            unspsc: it.unspsc,
            lead_time_days: it.lead,
            tax_amount: taxAmt,
            tax_description: taxAmt > 0 ? `Inclusive tax ${taxPct}%` : "",
          };
        }),
      };
      const { data } = await axios.post(
        `${API}/punchout/sub-supplier/${supplierId}/mock-cart`,
        payload
      );
      setReturned(data);
      toast.success(`${data.count} item(s) sent back to UniBuy™ cart`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cart return failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!sessionId || !supplierId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-bold text-slate-900">Invalid PunchOut Session</h1>
          <p className="text-sm text-slate-600 mt-2">
            This page requires a valid <code className="text-xs bg-slate-100 px-1 rounded">session_id</code> and{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">supplier_id</code> in the URL.
          </p>
          <Link to="/infoshop" className="mt-4 inline-block text-sm text-[#0072CE] font-semibold hover:underline">
            Return to UniBuy™
          </Link>
        </div>
      </div>
    );
  }

  if (returned) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="max-w-xl bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="mock-returned-confirm">
          <div className="h-12 w-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
            <Send size={20} />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">
            Cart sent back to UniBuy™
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {returned.count} item(s) from {supplier?.name} are now in your UniBuy™ cart with
            auto-created UniBuy™ Part Numbers. You can keep shopping the UniBuy™ catalog or
            transfer the unified cart back to your eProcurement system.
          </p>
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-left">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              UniBuy™ Part Numbers minted
            </div>
            <ul className="text-xs font-mono text-slate-700 space-y-0.5">
              {returned.items.map((i) => (
                <li key={i.infoshop_pn}>
                  {i.infoshop_pn} ← {i.external_sku} × {i.quantity}
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/infoshop"
            data-testid="mock-back-to-infoshop"
            className="mt-6 inline-flex items-center gap-1.5 bg-[#0072CE] hover:bg-[#005BA6] text-white font-semibold rounded-md px-5 py-2.5 text-sm"
          >
            <ArrowLeft size={14} /> Continue shopping in UniBuy™
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sub-supplier branded header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link
            to="/infoshop"
            data-testid="mock-back-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Back to UniBuy™
          </Link>
          <div className="ml-4 flex items-center gap-3">
            {supplier?.logo_url ? (
              <img src={supplier.logo_url} alt={supplier?.name} className="h-9 w-9 rounded-md border border-slate-200 bg-white object-contain p-0.5" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-[#0072CE]/10 text-[#0072CE] font-bold flex items-center justify-center">
                {supplier?.name?.slice(0, 2)?.toUpperCase() || "SS"}
              </div>
            )}
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">PunchOut Catalog</div>
              <div className="font-display font-bold text-slate-900">{supplier?.name || "Sub-supplier"}</div>
            </div>
          </div>
          <div className="ml-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            PunchOut session active
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8">
          <div className="mb-4">
            <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
              Search {supplier?.name}
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Pick items to send back to your UniBuy™ cart · {supplier?.currency} pricing
            </p>
          </div>

          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part number, MFG, description…"
              data-testid="mock-search-input"
              className="pl-9 h-10 bg-white border-slate-200"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {catalog.map((it) => (
              <CatalogItem
                key={it.sku}
                item={it}
                qty={qtys[it.sku] || 0}
                onChangeQty={(q) => setQtys((prev) => ({ ...prev, [it.sku]: q }))}
                supplier={supplier}
              />
            ))}
          </div>

          {!catalog.length && (
            <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
              No matching items in {supplier?.name}&apos;s mock catalog.
            </div>
          )}
        </section>

        <aside className="lg:col-span-4">
          <div className="sticky top-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <ShoppingCart size={14} className="text-[#0072CE]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Cart to send back
              </span>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto space-y-2">
              {cartItems.length === 0 && (
                <div className="text-xs text-slate-500 py-6 text-center">
                  Pick items from the left, they&apos;ll appear here.
                </div>
              )}
              {cartItems.map((it) => (
                <div key={it.sku} className="flex items-start gap-2" data-testid={`mock-cart-line-${it.sku}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-display font-semibold text-slate-900 leading-tight line-clamp-2">
                      {it.desc}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 mt-0.5">{it.sku}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {supplier?.currency} {it.price.toFixed(2)} × {it.qty} ={" "}
                      <span className="font-semibold text-slate-700">
                        {supplier?.currency} {(it.price * it.qty).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setQtys((prev) => ({ ...prev, [it.sku]: 0 }))}
                    data-testid={`mock-cart-remove-${it.sku}`}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 p-4 space-y-2">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-display font-bold text-lg text-slate-900" data-testid="mock-cart-total">
                  {supplier?.currency} {cartTotal.toFixed(2)}
                </span>
              </div>
              {/* Demo tax-mode toggle (v3.8.6) — drives whether the synthesised
                  PunchOutOrderMessage carries a <Tax> element. */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[11px] space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Tax mode (demo)
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="tax-mode"
                      value="none"
                      checked={taxMode === "none"}
                      onChange={() => setTaxMode("none")}
                      data-testid="mock-tax-mode-none"
                    />
                    <span className="text-slate-700">No tax sent</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="tax-mode"
                      value="incl"
                      checked={taxMode === "incl"}
                      onChange={() => setTaxMode("incl")}
                      data-testid="mock-tax-mode-incl"
                    />
                    <span className="text-slate-700">Inclusive</span>
                  </label>
                  {taxMode === "incl" && (
                    <span className="ml-auto inline-flex items-center gap-1">
                      <Input
                        value={taxPct}
                        onChange={(e) => setTaxPct(Number(e.target.value) || 0)}
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        data-testid="mock-tax-pct"
                        className="h-7 w-14 text-[11px] px-1.5"
                      />
                      <span className="text-slate-500">%</span>
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 leading-snug">
                  {taxMode === "none"
                    ? "Sub-supplier returns no <Tax> element — UniBuy™ cart will show \"Tax Not Included\"."
                    : `Sub-supplier returns <Tax> with ${taxPct}% — UniBuy™ cart will show \"Incl. tax\".`}
                </div>
              </div>
              <Button
                onClick={sendCartBack}
                disabled={cartItems.length === 0 || submitting}
                data-testid="mock-send-cart-btn"
                className="w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold disabled:opacity-40"
              >
                {submitting ? (
                  <><Loader2 size={14} className="animate-spin mr-2" /> Sending cXML…</>
                ) : (
                  <><Send size={14} className="mr-2" /> Send Cart to UniBuy™</>
                )}
              </Button>
              <p className="text-[10px] text-slate-500 leading-snug">
                Posts a PunchOutOrderMessage cXML 1.2.045 back to UniBuy™&apos;s
                BrowserFormPost URL. UniBuy™ will auto-create UniBuy™ Part
                Numbers for each line.
              </p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
